/**
 * Home page configuration data layer.
 *
 * WHY THIS TALKS TO THE SERVER NOW
 * This used to read and write `localStorage['mapgeoid-home-config']`, which
 * made the portal's branding a PER-BROWSER preference wearing the clothes of a
 * portal-wide setting. An admin changed the background, the colours, the logo;
 * it saved; it looked right — and nobody else ever saw it. Not their own
 * phone, not their own second laptop, not one other user. The setting was
 * never shared because it never left the machine it was set on.
 *
 * It now lives in one row on the server, so every device asks the same
 * question and gets the same answer. The access rule is enforced there too:
 * any signed-in user may read it, only an admin may write it.
 *
 * DEFAULTS LIVE HERE, NOT ON THE SERVER
 * The API answers `config: null` when no admin has chosen anything, and this
 * file merges that over `DEFAULT_HOME_CONFIG`. Keeping the defaults on one
 * side only means the palette on screen and the palette in the database can
 * never drift apart.
 */

import type { HomePageConfig } from '../types';
import { api, ApiError } from './apiClient';
import { readJson, removeKey } from './storage';

/** The key the old per-browser configuration was stored under. */
const LEGACY_KEY = 'mapgeoid-home-config';

/**
 * Fallbacks used when the admin has not overridden a text colour.
 * The eyebrow deliberately tracks the accent colour unless overridden.
 */
export const DEFAULT_TITLE_COLOR = '#F8FAFC';
export const DEFAULT_SUBTITLE_COLOR = 'rgba(203,225,250,0.72)';
export const DEFAULT_HEADER_COLOR = 'rgba(7,26,51,0.72)';

export const DEFAULT_HOME_CONFIG: HomePageConfig = {
  eyebrow: 'GEOSPATIAL INTELLIGENCE PLATFORM',
  title: 'MapGeoid GIS Portal',
  subtitle: 'Build, configure, and publish spatial data modules from a single workspace.',
  modulesSectionLabel: 'YOUR MODULES',
  addModuleButtonLabel: 'Add Module',
  accentColor: '#2DD4BF',
  backgroundOverlay: 0.8,
};

interface HomeConfigResponse {
  /** Null until an admin has saved something. */
  config: Partial<HomePageConfig> | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Merged so a config saved before a field existed still picks up its default. */
function withDefaults(stored: Partial<HomePageConfig> | null | undefined): HomePageConfig {
  return { ...DEFAULT_HOME_CONFIG, ...(stored ?? {}) };
}

/**
 * The branding this browser saved back when the setting was per-browser.
 *
 * Read once, on the way to the server. See `adoptLegacyConfig`.
 */
function readLegacyConfig(): Partial<HomePageConfig> | null {
  const stored = readJson<Partial<HomePageConfig> | null>(LEGACY_KEY, null);
  return stored && typeof stored === 'object' && Object.keys(stored).length > 0 ? stored : null;
}

/**
 * Carries a pre-existing local configuration up to the server, once.
 *
 * WHY THIS IS WORTH THE COMPLICATION
 * Without it, the fix looks like a regression to the one person most affected
 * by it: the admin who had already styled the portal opens it after the
 * deploy, the server has no row yet, and their logo and colours are simply
 * gone. They did not lose anything — it is still in their browser — but it is
 * not on screen, which is the only thing that matters to them.
 *
 * Only an admin can do this (the server refuses anyone else), only when the
 * server has nothing yet, and only once — the local copy is dropped
 * afterwards so a stale browser can never re-seed a branding an admin has
 * since changed deliberately.
 */
async function adoptLegacyConfig(isAdmin: boolean): Promise<HomePageConfig | null> {
  const legacy = readLegacyConfig();
  if (!legacy) return null;

  if (!isAdmin) {
    // A non-admin's stale copy is not the portal's branding and never was.
    // Dropped rather than uploaded, so it stops shadowing the real answer.
    removeKey(LEGACY_KEY);
    return null;
  }

  try {
    const saved = await settingsService.updateHomeConfig(withDefaults(legacy));
    removeKey(LEGACY_KEY);
    return saved;
  } catch {
    // Left in place to retry on the next load. A failed migration must not
    // throw away the only copy of the branding that exists.
    return null;
  }
}

export const settingsService = {
  /**
   * The portal's branding.
   *
   * `isAdmin` is only used to decide whether this browser's pre-existing local
   * configuration may be adopted as the portal's; reading never depends on it.
   */
  async getHomeConfig(isAdmin = false): Promise<HomePageConfig> {
    let response: HomeConfigResponse;
    try {
      response = await api.get<HomeConfigResponse>('/settings/home');
    } catch (error) {
      /* The portal must still render. Branding is the least important thing on
         the page, and failing to fetch it should never be what stops someone
         reaching their modules — so this falls back to the defaults and says
         so in the console rather than throwing into the app. */
      console.error('[settings] home configuration could not be loaded', error);
      return DEFAULT_HOME_CONFIG;
    }

    if (response.config === null) {
      const adopted = await adoptLegacyConfig(isAdmin);
      if (adopted) return adopted;
    } else {
      // The server is authoritative now; a leftover local copy would only be
      // a trap for the next person reading this code.
      removeKey(LEGACY_KEY);
    }

    return withDefaults(response.config);
  },

  async updateHomeConfig(config: HomePageConfig): Promise<HomePageConfig> {
    const next = withDefaults(config);
    try {
      const response = await api.put<HomeConfigResponse>('/settings/home', next);
      return withDefaults(response.config);
    } catch (error) {
      throw new Error(describeSaveFailure(error));
    }
  },

  async resetHomeConfig(): Promise<HomePageConfig> {
    try {
      await api.delete<HomeConfigResponse>('/settings/home');
    } catch (error) {
      throw new Error(describeSaveFailure(error));
    }
    removeKey(LEGACY_KEY);
    return DEFAULT_HOME_CONFIG;
  },
};

/** Turns an API failure into a line that says what to do about it. */
function describeSaveFailure(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.unauthenticated) {
      return 'Your session has expired. Sign in again to save these settings.';
    }
    if (error.status === 403) {
      return 'Only an administrator can change the portal home page.';
    }
    if (error.status === 413 || error.status === 422) {
      // The one field big enough to hit a limit is an embedded image.
      return `${error.message} If an uploaded image is the problem, use a smaller one or paste an image URL instead.`;
    }
    if (error.status === 0) {
      return 'Could not reach the server, so nothing was saved. Check your connection and try again.';
    }
    return error.message;
  }
  return 'Could not save changes. Please try again.';
}
